import { useEffect, useState } from 'react';
import "./CallParams.css";
import { 
    TAPIResponseWithAbi, 
    TChainName, 
    TContractCallParams, 
    TCustomError, 
    TOutputType, 
    TParsedFragment, 
    TParsedContractCallParams 
} from '../../types';
import {
    getContractAbi, 
    getFunctionSelector, 
    encodeCallParams,
    parseAbi, 
    readSettingsFromFile, 
    saveSettingsToFile, 
    stringToStringArray 
} from '../../utils';
import { emptyParsedParams } from '../../types/constants';
import { getImplementation } from '../../utils/getImplementation';

export default function CallParam(params: {
    chainName: TChainName;
    index: number;
    key: number;
    onChange: (index:number, item: TContractCallParams, parsed: TParsedContractCallParams) => void;
    removeParam: (index: number) => void;
    value: TContractCallParams;
}) {

    const [value, setValue] = useState<TContractCallParams>(params.value);
    const [encodedParams, setEncodedParams] = useState<TParsedContractCallParams>(emptyParsedParams);
    const [err, setErr] = useState<string>('');
    const [selectedOption, setSelectedOption] = useState<number>();

    const getAbi = () => {
        (async () => {

            const result: TAPIResponseWithAbi = await getContractAbi(params.chainName, value.contr);

            if (result && result.abi) {
                console.log(result.abi)
                const fragments: TParsedFragment[] = parseAbi(result.abi);
                setValue({ ...value, abi: fragments });
                setErr(result.error);
            }

        })().catch((error: TCustomError) => {
            setValue({ ...value, abi: [] });
            setErr(error.message)
        })
    }

    const getImplementationAbi = () => {
        (async () => {
            const implAddress = await getImplementation(value.contr, params.chainName)
            console.log('implAddress:', implAddress)
            const result: TAPIResponseWithAbi = await getContractAbi(params.chainName, implAddress);

            if (result && result.abi) {
                console.log(result.abi)
                const fragments: TParsedFragment[] = parseAbi(result.abi);
                setValue({ ...value, abi: fragments });
                setErr(result.error);
            }

        })().catch((error: TCustomError) => {
            setValue({ ...value, abi: [] });
            setErr(error.message)
        })
    }

    const handleUploadAbiClick = () => {
        (async () => {

            const abiJson: any = await readSettingsFromFile();

            if (abiJson) {
                setValue({ ...value, abi: abiJson });
            }

        })().catch((e: TCustomError) => {
            console.error("uploadSettings", e)
        });
    }

    const handleDownloadAbiClick = () => {
        (async () => {
            if (value.abi) {
                saveSettingsToFile(value.abi);
            }
        })().catch((e: TCustomError) => {
            console.error("uploadSettings", e)
        });
    }

    const populateFromAbi = (index: number) => {
        setSelectedOption(index);
        if (value.abi && value.abi.length) {

            const fragment: TParsedFragment = value.abi[index];

            if (fragment) {

                if (fragment.outputs.length > 0) {
                    setValue({
                        ...value,
                        func: fragment.name,
                        types: fragment.types ? fragment!.types : fragment.inputs!.map((input: {type: string}) => input.type),
                        params: [],
                        isResultTuple: true,
                        returnTypes: fragment.outputs
                    });
                } else {
                    setValue({
                        ...value, 
                        func: fragment.name,
                        types: fragment.types ? fragment!.types : fragment.inputs!.map((input: {type: string}) => input.type),
                        params: [],
                        isResultTuple: false
                    });
                }
            }
        }
    }

    // Updates the global state from the local
    useEffect(() => {

        let prepared: TParsedContractCallParams = emptyParsedParams;

        try {

            const decimals: number = value.decimals ? value.decimals : 1;

            prepared = {
                contr: value.contr ? value.contr : emptyParsedParams.contr,
                func: value.func ? getFunctionSelector(value.func, value.types as string[]) : emptyParsedParams.func,
                // @ts-ignore
                params: value.params.length > 0 ? encodeCallParams(value.types,value.params) : '0x',
                extractItem: value.extractItem ? value.extractItem : emptyParsedParams.extractItem,
                isResultTuple: value.isResultTuple ? value.isResultTuple : emptyParsedParams.isResultTuple,
                isParam: value.isParam ? value.isParam : emptyParsedParams.isParam,
                value: value.isParam && value.value ? value.value * decimals : 0
            }
    
            // console.log('prepared', prepared)
            setEncodedParams(prepared);
            setErr('');
            
        } catch (error: {message:string} | any) {
            setErr(error.message)
            console.error("CallParam::Encoding", error)
        }

        params.onChange(params.index, value, prepared);

    }, [value]);

    // Updates local state from the global
    useEffect(() => {
        setValue(params.value)
    }, [params.value]);

    useEffect(() => {

        if(value && value.abi && value.abi.length == 1){
            populateFromAbi(0)
        }

    }, [value.abi])

    return (<div className='call-params-container'>
        <div className=''>
            <div className='threeColumnGrid'>
                <label id="isParamLabel" htmlFor='isParam'>Is Param:</label>
                <div className='rowGroup '>
                    <input
                        type='checkbox'
                        id='isParam'
                        checked={value.isParam}
                        onChange={(e) => setValue({ ...value, isParam: e.target.checked })}
                    />
                    {value.isParam ? (<div>
                        <label htmlFor='value'>Value:</label>
                        <input
                            className='shortInput'
                            type='number'
                            id='value'
                            value={value.value}
                            onChange={(e) => setValue({ ...value, value: parseInt(e.target.value) })}
                        />
                        <select
                            className='decimals'
                            onChange={(e) => setValue({...value, decimals: parseInt(e.target.value)})}
                        >
                            <option
                                value={1}
                            >
                                decimals
                            </option>
                            <option
                                value={1e6}
                            >
                                10^6
                            </option>
                            <option
                                value={1e8}
                            >
                                10^8
                            </option>
                            <option
                                value={1e10}
                            >
                                10^10
                            </option>
                            <option
                                value={1e12}
                            >
                                10^12
                            </option>
                            <option
                                value={1e14}
                            >
                                10^14
                            </option>
                            <option
                                value={1e16}
                            >
                                10^16
                            </option>
                            <option
                                value={1e18}
                            >
                                10^18
                            </option>
                        </select>
                    </div>)
                        : (<div className='gridPlaceholder'></div>)}
                </div>
                <div className='alignRight'>
                    Var # {params.index}
                </div>
            </div>

            {!value.isParam && (
                <div>
                    <div className='threeColumnGrid'>
                        <div className='twoColumnGrid'>
                            <label htmlFor='contr'>Contract:</label>
                            <input
                                type='text'
                                id='contr'
                                value={value.contr}
                                onChange={(e) => setValue({ ...value, contr: e.target.value })}
                            />
                        </div>
                    </div>
                    <div className='threeColumnGrid'>
                        <div className='twoColumnGrid'>
                            <label htmlFor='funcName'>Function:</label>
                            <input
                                type='text'
                                id='funcName'
                                value={value.func}
                                onChange={(e) => setValue({ ...value, func: e.target.value })}
                            />
                        </div>
                    </div>
                    <div className='threeColumnGrid'>
                        <div className='twoColumnGrid'>
                            <label htmlFor='types'>Types:</label>
                            <input
                                type='text'
                                id='types'
                                value={value.types}
                                onChange={(e) => setValue({ ...value, types: stringToStringArray(e.target.value) })}
                            />
                        </div>
                    </div>
                    <div className='threeColumnGrid'>
                        <div className='twoColumnGrid'>
                            <label htmlFor='params'>Params:</label>
                            <input
                                type='text'
                                id='params'
                                value={value.params}
                                onChange={(e) => setValue({ ...value, params: stringToStringArray(e.target.value) })}
                            />
                        </div>
                    </div>
                    <div className='twoColumnGrid'>
                        <label htmlFor='isResultTuple'>Is Result Tuple:</label>
                        <div className=''>
                            <div className='inlineThreeColGrid'>
                                <input
                                    type='checkbox'
                                    id='isResultTuple'
                                    checked={value.isResultTuple}
                                    onChange={(e) => setValue({ ...value, isResultTuple: e.target.checked })}
                                />
                                {value.isResultTuple && (<label htmlFor='extractItem'>Extract Item:</label>)}
                                {value.isResultTuple && (
                                    <select onChange={(e) => setValue({ ...value, extractItem: parseInt(e?.target.value) })}>
                                        {value.returnTypes && value.returnTypes.map((returnType: TOutputType, index: number) => (
                                            <option
                                                key={index}
                                                value={index}
                                            >
                                                {returnType.type + ' ' + returnType.name}
                                            </option>))}
                                    </select>)}
                            </div>

                        </div>
                    </div>
                </div>)}

            <div className='CallParamsContainer'>
                <div className='twoColumnGrid'>
                    <button
                        onClick={() => params.removeParam(params.index)}
                    >Remove
                    </button>
                    {!value.isParam && (<div className='rowGroup justifySpaceBetween'>
                        {value.abi && value.abi.length
                            ? (<div>
                                <select
                                    onChange={(e) => populateFromAbi(parseInt(e?.target.value))}
                                    value={selectedOption}>
                                    {value.abi && value.abi.length && value.abi.map((fragment: TParsedFragment, index: number) => (
                                        <option
                                            key={index}
                                            value={index}>
                                            {fragment.name}
                                        </option>
                                    ))}
                                </select>
                                <button
                                    onClick={handleDownloadAbiClick}
                                >
                                    Download ABI
                                </button>
                            </div>

                            )
                            : (<div>
                                <button
                                    onClick={getAbi}
                                >
                                    Fetch ABI
                                </button>
                                <button
                                    onClick={getImplementationAbi}
                                >
                                    Fetch Implementation ABI
                                </button>
                                <button
                                    onClick={handleUploadAbiClick}
                                >
                                    Upload ABI
                                </button>
                            </div>

                            )
                        }

                    </div>)

                    }

                </div>

            </div>
        </div >
        {err && !value.isParam
            && <div className='CallParamsContainer'>
                <div className='twoColumnGrid'>
                    <label className='width390' htmlFor="">Error:</label>
                    <div className='redFrame'>
                        {err}
                    </div>
                </div>
            </div>}
    </div >)
}
